import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { labelFor, statusCls } from "@/lib/categories";
import { IMAGES } from "@/lib/images";
import { useLang } from "@/context/LanguageContext";
import { FormHeader } from "@/components/FormShell";
import { Check, Circle, PackageSearch, ShieldCheck, Image as ImageIcon, KeyRound, Truck, Copy } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function Track() {
  const { declarationId } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [id, setId] = useState(declarationId || "");
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverPhone, setRecoverPhone] = useState("");
  const [recoverMsg, setRecoverMsg] = useState("");
  const [recovering, setRecovering] = useState(false);
  const [signing, setSigning] = useState(false);
  const sigRef = useRef(null);

  async function submitSignature() {
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error(t("trk.signFirst")); return; }
    setSigning(true);
    try {
      await api(`/claim/${data.id}/signature-public?email=${encodeURIComponent(email)}`, {
        method: "POST", auth: false, body: { signature_base64: sigRef.current.getDataURL() },
      });
      toast.success(t("trk.sigSaved"));
      await fetchTrack(data.id, email);
    } catch (e) { toast.error(e.message); } finally { setSigning(false); }
  }

  async function fetchTrack(theId, theEmail, opts = {}) {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const q = theEmail ? `?email=${encodeURIComponent(theEmail)}` : "";
      setData(await api(`/declarations/${theId}/track${q}`, { auth: false }));
    } catch (e) {
      if (!silent) { toast.error(e.message); setData(null); }
    } finally { if (!silent) setLoading(false); }
  }

  async function recoverCode(e) {
    e.preventDefault();
    if (!recoverPhone.trim()) return;
    setRecovering(true);
    try {
      const res = await api(`/claim/recover-by-phone`, { method: "POST", auth: false, body: { phone: recoverPhone.trim() } });
      setRecoverMsg(res?.message || t("hc.smsSentIfAccount"));
    } catch (err) { toast.error(err.message); } finally { setRecovering(false); }
  }

  useEffect(() => { if (declarationId) fetchTrack(declarationId); }, [declarationId]);

  // Auto-refresh: poll silently every 15s so the timeline advances on its own
  // (e.g. when the partner confirms a match) without a manual reload. Stops once
  // the declaration reaches a terminal state.
  const TERMINAL_STATUSES = ["restituted", "delivered", "closed", "resolved", "cancelled_by_customer"];
  useEffect(() => {
    if (!data?.id || TERMINAL_STATUSES.includes(data.status)) return;
    const iv = setInterval(() => { fetchTrack(data.id, email, { silent: true }); }, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.status, email]);

  function submit(e) {
    e.preventDefault();
    if (!id.trim()) return;
    navigate(`/track/${id.trim()}`);
    fetchTrack(id.trim(), email);
  }

  return (
    <div className="bg-background">
      <div className="relative h-56 overflow-hidden photo-scrim">
        <img src={IMAGES.hallway} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
          <span className="inline-flex items-center gap-2 rounded-full badge-red px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">{t("trk.badge")}</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold">{t("trk.title")}</h1>
          <p className="mt-2 text-white/85 max-w-lg">{t("trk.subtitle")}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
      <form onSubmit={submit} className="findo-numbered form-card mt-8 bg-white rounded-3xl border border-border overflow-hidden p-6 space-y-6" data-testid="track-form">
        <FormHeader icon={PackageSearch} title={t("trk.title")} subtitle={t("trk.subtitle")} className="-mx-6 -mt-6 mb-2" />
        <div><Label>{t("trk.idLabel")}</Label><Input required value={id} onChange={(e) => setId(e.target.value)} placeholder={t("hc.exId")} className="mt-1.5 h-11 rounded-xl font-mono" data-testid="track-id" /></div>
        <div><Label>{t("trk.emailLabel")}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="track-email" /></div>
        <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl font-semibold" data-testid="track-submit">{loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("trk.searching")}</span> : t("trk.submit")}</Button>
        <button type="button" onClick={() => { setRecoverOpen((o) => !o); setRecoverMsg(""); }}
          className="w-full text-center text-sm font-semibold text-findo-indigo hover:underline" data-testid="track-lost-email-link">
          {t("trk.lostEmail")}
        </button>
        {recoverOpen && (
          <div className="rounded-xl border border-findo-indigo/20 bg-findo-indigo/[0.04] p-4 space-y-2.5" data-testid="track-recover-block">
            <p className="text-xs text-slate-600 flex items-center gap-1.5"><KeyRound size={14} className="text-findo-indigo" /> {t("trk.recoverHint")}</p>
            {recoverMsg ? (
              <p className="text-sm font-semibold text-emerald-700" data-testid="track-recover-msg">{recoverMsg}</p>
            ) : (
              <div className="flex gap-2">
                <Input value={recoverPhone} onChange={(e) => setRecoverPhone(e.target.value)} placeholder={t("trk.phonePh")} className="h-10 rounded-xl" data-testid="track-recover-phone" />
                <Button type="button" onClick={recoverCode} disabled={recovering || !recoverPhone.trim()} className="rounded-xl font-semibold" data-testid="track-recover-btn">
                  {recovering ? "…" : t("trk.send")}
                </Button>
              </div>
            )}
          </div>
        )}
      </form>

      {data && (
        <div className="mt-8 bg-white rounded-2xl border border-border p-6 card-shadow animate-fade-up" data-testid="track-result">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400">{labelFor(data.category)}</p>
              <p className="font-semibold text-slate-800">{data.description}</p>
              <p className="text-xs text-slate-400 mt-1">{t("trk.at")} {data.partner?.name}</p>
            </div>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls(data.status)}`}>{t(`st.${data.status}`)}</span>
          </div>
          <div className="mt-6 space-y-0.5">
            {(data.timeline || data.events || []).map((ev, i) => (
              <div key={ev.key} className="flex gap-3" data-testid={`timeline-${ev.key}`}>
                <div className="flex flex-col items-center">
                  <span className={`h-7 w-7 rounded-full grid place-items-center ${ev.done ? "findo-gradient text-white" : (!ev.done && ev.pending ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-300")}`}>
                    {ev.done ? <Check size={15} /> : (!ev.done && ev.pending ? <FindoSpinner size={13} /> : <Circle size={10} />)}
                  </span>
                  {i < (data.timeline || data.events || []).length - 1 && <span className={`w-0.5 flex-1 min-h-6 ${ev.done ? "bg-findo-indigo/30" : "bg-slate-100"}`} />}
                </div>
                <div className="pb-5">
                  <p className={`font-semibold text-sm ${ev.done ? "text-slate-800" : (!ev.done && ev.pending ? "text-amber-700" : "text-slate-400")}`}>
                    {(() => { const l = t(`ev.${ev.key}`); return l === `ev.${ev.key}` ? ev.label : l; })()}
                    {ev.done && ev.external && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-findo-indigo/10 text-findo-indigo text-[11px] font-bold px-2 py-0.5" data-testid={`timeline-external-badge-${ev.key}`}>
                        {t("hc.paidViaPartner")}
                      </span>
                    )}
                  </p>
                  {!ev.done && ev.pending && (
                    <p className="text-xs text-amber-600 mt-0.5" data-testid={`timeline-pending-${ev.key}`}>
                      {(() => { const l = t("trk.matchVerifying"); return l === "trk.matchVerifying" ? t("hc.potentialMatchBeingVerified") : l; })()}
                    </p>
                  )}
                  {ev.at && <p className="text-xs text-slate-400">{new Date(ev.at).toLocaleString(document.documentElement.lang || "fr-FR")}</p>}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500 flex items-start gap-1.5" data-testid="track-fee-recipient-note">
            <ShieldCheck size={13} className="shrink-0 mt-0.5 text-findo-indigo" />
            {data.recovery_free
              ? <>{t("hc.recoveryOfferedByTheEstablishment")}</>
              : <>{t("hc.recoveryFeesArePaidDirectly")}</>}
          </p>
          {data.claim && (data.claim.code || data.claim.photo_available) && (
            <div className="mt-4 rounded-xl border border-findo-indigo/20 bg-findo-indigo/[0.04] p-4 space-y-3" data-testid="track-claim-block">
              <p className="text-sm font-bold text-findo-indigo flex items-center gap-1.5"><ShieldCheck size={16} /> {t("trk.secureRest")}</p>
              {data.claim.code ? (
                <div data-testid="track-claim-code">
                  <p className="text-xs text-slate-500">{t("trk.yourCode")}</p>
                  <p className="mt-1 text-2xl font-bold tracking-[0.3em] text-findo-indigo font-mono bg-white rounded-lg border border-findo-indigo/20 py-2.5 text-center">{data.claim.code}</p>
                  {data.claim.has_qr && (
                    <div className="mt-2 flex flex-col items-center">
                      <img src={`${API}/claim/${data.id}/code-qr.png?email=${encodeURIComponent(email)}`} alt={t("pub.claimQr")}
                        className="h-32 w-32 rounded-lg border border-findo-indigo/20 bg-white p-1.5" data-testid="track-claim-qr"
                        onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                      <span className="text-[11px] text-slate-400 mt-1">{t("trk.showQr")}</span>
                    </div>
                  )}
                  {data.claim.verified && <p className="mt-1.5 text-xs font-semibold text-emerald-700 flex items-center gap-1"><Check size={13} /> {t("trk.codeValidated")}</p>}
                </div>
              ) : (
                <p className="text-xs text-slate-500">{t("trk.enterEmailCode")}</p>
              )}
              {data.claim.photo_available && email && (
                <div data-testid="track-claim-photo">
                  <p className="text-xs text-slate-500 flex items-center gap-1.5 mb-1.5"><ImageIcon size={13} /> {t("trk.photoBefore")}</p>
                  <img src={`${API}/claim/${data.id}/photo-public?email=${encodeURIComponent(email)}`} alt="" className="w-full max-h-64 object-contain rounded-lg border border-border bg-white"
                    onError={(ev) => { ev.currentTarget.style.display = "none"; }} data-testid="track-claim-photo-img" />
                </div>
              )}
            </div>
          )}
          {data.is_shipping_paid ? (
            <button disabled className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-100 text-emerald-700 font-semibold px-5 py-2.5 cursor-default" data-testid="track-pay-shipping-btn">
              <Truck size={17} /> {t("hc.shippingPaid")}
            </button>
          ) : data.awaiting_shipping_payment ? (
            <button onClick={() => navigate(`/ship/${data.id}`)} className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-amber-500 text-white font-semibold px-5 py-2.5 hover:bg-amber-600 transition-colors" data-testid="track-pay-shipping-btn">
              <Truck size={17} /> {t("hc.payTheShippingFees")}
            </button>
          ) : null}

          {(data.shipment || ["shipping_requested", "shipping_paid", "shipped", "delivered"].includes(data.status)) && (
            <div className="mt-4 rounded-xl border border-findo-indigo/20 bg-findo-indigo/[0.04] p-4 space-y-2.5" data-testid="track-shipment-block">
              <p className="text-sm font-bold text-findo-indigo flex items-center gap-1.5"><Truck size={16} /> {t("trk.shipTitle")}</p>
              {(() => {
                const st = data.shipment?.status;
                const map = {
                  pending: { l: t("hc.awaitingPayment"), c: "bg-amber-100 text-amber-700" },
                  paid: { l: t("hc.paymentReceived"), c: "bg-blue-100 text-blue-700" },
                  generating: { l: t("hc.labelBeingGenerated"), c: "bg-blue-100 text-blue-700" },
                  label_generated: { l: t("hc.parcelBeingPrepared"), c: "bg-indigo-100 text-indigo-700" },
                  shipped: { l: t("hc.parcelShipped"), c: "bg-emerald-100 text-emerald-700" },
                  delivered: { l: t("hc.parcelDelivered"), c: "bg-emerald-100 text-emerald-700" },
                };
                const s = map[st];
                return s ? (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">{t("hc.status")}</span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.c}`} data-testid="track-ship-status">{s.l}</span>
                  </div>
                ) : null;
              })()}
              {data.shipment?.tracking_number ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">{t("trk.carrier")}</span>
                    <span className="text-sm font-semibold text-slate-800" data-testid="track-ship-carrier">{data.shipment.carrier || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">{t("trk.trackingNo")}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-mono font-bold text-findo-indigo" data-testid="track-ship-tracking">
                      {data.shipment.tracking_number}
                      <button type="button" onClick={() => { navigator.clipboard?.writeText(data.shipment.tracking_number); toast.success(t("an.linkCopied")); }}
                        className="text-slate-400 hover:text-findo-navy" title={t("an.copyLink")} data-testid="track-ship-copy"><Copy size={13} /></button>
                    </span>
                  </div>
                  {data.shipment.tracking_url && (
                    <a href={data.shipment.tracking_url} target="_blank" rel="noreferrer"
                      className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-findo-indigo text-white font-semibold text-sm py-2.5 hover:bg-findo-indigo/90"
                      data-testid="track-ship-follow">
                      <Truck size={15} /> {t("trk.trackShipment")}
                    </a>
                  )}
                </>
              ) : (
                <p className="text-xs text-slate-500" data-testid="track-ship-pending">{t("trk.shipPending")}</p>
              )}
            </div>
          )}
          {["restituted", "shipped", "delivered"].includes(data.status) && email && (
            data.claim?.signed ? (
              <a href={`${API}/claim/${data.id}/restitution-receipt-public.pdf?email=${encodeURIComponent(email)}`}
                target="_blank" rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-findo-indigo/30 text-findo-indigo font-semibold text-sm px-4 py-2.5 hover:bg-findo-indigo/5"
                data-testid="track-restitution-receipt">
                <PackageSearch size={15} /> {t("trk.dlProof")}
              </a>
            ) : (
              <div className="mt-4 rounded-xl border border-findo-indigo/20 bg-findo-indigo/[0.04] p-4" data-testid="track-signature-block">
                <p className="text-sm font-bold text-findo-indigo flex items-center gap-1.5 mb-2"><PackageSearch size={16} /> {t("trk.signProof")}</p>
                <SignaturePad ref={sigRef} />
                <Button onClick={submitSignature} disabled={signing} className="mt-2 w-full h-10 rounded-xl font-semibold" data-testid="track-signature-submit">
                  {signing ? "…" : t("trk.signSubmit")}
                </Button>
              </div>
            )
          )}
          {data.status === "matched" && (
            <Button onClick={() => navigate(`/verifier/${data.id}`)} className="w-full rounded-xl font-semibold mt-2" data-testid="track-pay-btn">{t("trk.recoverBtn")}</Button>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
